import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Eye, Edit, Shield, Lock, Sun, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const AdminServices = () => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('services');

  const languages = ['sk', 'en', 'de', 'cs', 'ru'];
  const languageNames = { sk: 'SK', en: 'EN', de: 'DE', cs: 'CS', ru: 'RU' };
  const [activeLang, setActiveLang] = useState('sk');

  // Services content - in real app would be stored in DB and loaded
  const [services, setServices] = useState([
    {
      id: 'service1',
      icon: 'shield',
      translations: {
        sk: {
          title: 'Bezpečnostná fólia s ochranou proti slnku',
          subtitle: 'Maximálna ochrana a komfort v jednom riešení',
          description: 'Bezpečnostné fólie na okná spevňujú sklo a poskytujú dodatočnú odolnosť proti nárazom, rozbitiu či vlámaniu.',
          benefits: ['Ochrana pred krádežami', 'Zníženie teploty', 'UV blok', 'Ochrana súkromia']
        },
        en: {
          title: 'Security Film with Sun Protection',
          subtitle: 'Maximum protection and comfort in one solution',
          description: 'Security films for windows strengthen glass and provide additional resistance against impacts.',
          benefits: ['Protection against theft', 'Heat reduction', 'UV block', 'Privacy protection']
        }
      }
    },
    {
      id: 'service2',
      icon: 'lock',
      translations: {
        sk: {
          title: 'Fólie pre ochranu súkromia',
          subtitle: 'Viac súkromia bez výmeny skla',
          description: 'Dekoratívne fólie s matným alebo vzorovaným povrchom vytvárajú elegantný efekt.',
          benefits: ['Rýchla aplikácia', 'Vhodné do prenajatých priestorov', 'Nepriehľadnosť', 'Elegantný dizajn']
        },
        en: {
          title: 'Privacy Protection Films',
          subtitle: 'More privacy without replacing glass',
          description: 'Decorative films with matte or patterned surface create an elegant effect.',
          benefits: ['Quick application', 'Suitable for rental spaces', 'Opacity', 'Elegant design']
        }
      }
    },
    {
      id: 'service3',
      icon: 'sun',
      translations: {
        sk: {
          title: 'Tepelno-izolačné fólie',
          subtitle: 'Úspora energie a pohodlie počas celého roka',
          description: 'Fólie regulujú teplo a znižujú jeho prestup cez okná.',
          benefits: ['Redukcia tepla', 'UV ochrana', 'Vhodné pre domácnosti', 'Jasné výhľady']
        },
        en: {
          title: 'Thermal Insulation Films',
          subtitle: 'Energy savings and comfort all year round',
          description: 'Films regulate heat and reduce its transfer through windows.',
          benefits: ['Heat reduction', 'UV protection', 'Suitable for homes', 'Clear views']
        }
      }
    },
    {
      id: 'service4',
      icon: 'zap',
      translations: {
        sk: {
          title: 'Smart fólie – prepínateľné sklo',
          subtitle: 'Súkromie na dotyk tlačidla',
          description: 'Inovatívne PDLC fólie menia svoju priehľadnosť podľa potreby.',
          benefits: ['Rýchly prechod', 'Dodatočná montáž', 'Diaľkové ovládanie', 'Ľubovoľný výrez']
        },
        en: {
          title: 'Smart Films – Switchable Glass',
          subtitle: 'Privacy at the touch of a button',
          description: 'Innovative PDLC films change their transparency as needed.',
          benefits: ['Quick transition', 'Retrofit installation', 'Remote control', 'Custom cut']
        }
      }
    }
  ]);

  const iconComponents = {
    shield: Shield,
    lock: Lock,
    sun: Sun,
    zap: Zap
  };

  const handleSave = async () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 500);
  };

  const updateService = (serviceId, field, value) => {
    setServices(prev => prev.map(s => {
      if (s.id === serviceId) {
        return {
          ...s,
          translations: {
            ...s.translations,
            [activeLang]: {
              ...s.translations[activeLang],
              [field]: value
            }
          }
        };
      }
      return s;
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('nav.services')}</h1>
        <div className="flex items-center gap-3">
          <a 
            href="/services" 
            target="_blank" 
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500"
          >
            <Eye className="h-4 w-4" />
            {t('admin.services_page.view_page', 'Zobraziť stránku')}
          </a>
          <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? t('admin.saving') : <><Save className="h-4 w-4 mr-2" />{t('admin.save')}</>}
          </Button>
        </div>
      </div>

      {saved && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
          {t('admin.services_page.saved', '✓ Služby boli uložené')}
        </div>
      )}

      {/* Language Tabs */}
      <div className="flex gap-2 mb-6">
        {languages.map(lang => (
          <button
            key={lang}
            onClick={() => setActiveLang(lang)}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeLang === lang 
                ? 'bg-orange-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {languageNames[lang]}
          </button>
        ))}
      </div>

      {/* Services List */}
      <div className="space-y-6">
        {services.map((service, index) => {
          const Icon = iconComponents[service.icon] || Shield;
          const trans = service.translations[activeLang] || service.translations.sk || {};
          
          return (
            <div key={service.id} className="bg-white rounded-xl shadow p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Icon className="h-6 w-6 text-orange-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{t('admin.services_page.service_n', 'Služba')} {index + 1}</h3>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.services_page.field_name', 'Názov')}</label>
                  <Input
                    value={trans.title || ''}
                    onChange={(e) => updateService(service.id, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Podnadpis</label>
                  <Input
                    value={trans.subtitle || ''}
                    onChange={(e) => updateService(service.id, 'subtitle', e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Popis</label>
                <textarea
                  className="w-full border rounded-lg p-3 text-sm"
                  rows={3}
                  value={trans.description || ''}
                  onChange={(e) => updateService(service.id, 'description', e.target.value)}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">{t('admin.services_page.benefits_label', 'Výhody (oddelené čiarkou)')}</label>
                <Input
                  value={(trans.benefits || []).join(', ')}
                  onChange={(e) => updateService(service.id, 'benefits', e.target.value.split(',').map(b => b.trim()))}
                  placeholder={t('admin.services_page.benefits_ph', 'Výhoda 1, Výhoda 2, Výhoda 3')}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>{t('admin.services_page.note_label', 'Poznámka')}:</strong> {t('admin.services_page.note_text', 'Obsah stránky Služby je momentálne definovaný v prekladových súboroch (i18n).')}
          {t('admin.services_page.note_text2', 'Pre plnú funkčnosť databázového ukladania kontaktujte vývojára.')}
        </p>
      </div>
    </div>
  );
};

export default AdminServices;
